"use client";

import CardStatistics from "@/components/cardStatistics";
import FilterDate from "@/components/form/filterDate";
import Modal from "@/components/modal";
import NotFound from "@/components/notFound";
import Pagination from "@/components/pagination";
import Spinner from "@/components/spinner/loading";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { useApiReady } from "@/hooks/useApiReady";
import { usePermission } from "@/hooks/usePermission/usePermission";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchWalletStatement,
  fetchWalletStatementBySource,
} from "@/store/modules/subscriptionWallet/thunks";
import type { DictionaryProps } from "@/types";
import { formatCurrency } from "@/utils/formatCurrency";
import { isScheduleCheckoutFeatureEnabled } from "@/utils/scheduleCheckoutFeature";
import { CalendarClock, TrendingDown, TrendingUp, Wallet } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

type DateRange = { start: Date; end: Date };
type SourceTabKey = "asaas" | "manual" | "pix" | "checkout";
type TabKey = "all" | SourceTabKey;
type PaginationState = Record<SourceTabKey, { page: number; perPage: number }>;

type PaginationMeta = {
  page?: number;
  per_page?: number;
  total?: number;
  total_pages?: number;
};

type StatementBySourceEntry = {
  payment_id?: string;
  date?: string;
  amount?: number;
  released_amount?: number | null;
  split_amount?: number | null;
  status?: string;
  release_date?: string | null;
  released_at?: string | null;
  description?: string;
  client_name?: string;
  plan_name?: string;
  payment_method?: string;
  gateway?: string;
  schedule_id?: number | null;
  schedule_at?: string | null;
};

type PixWithdrawalItem = {
  requested_at?: string;
  processed_at?: string | null;
  amount?: number;
  status?: string;
  transfer_type?: string;
  key_type?: string;
  key?: string;
};

type WalletStatementItem = {
  id?: string;
  date?: string;
  description?: string;
  value?: number;
  signed_value?: number;
  status?: string;
  transaction_type?: string;
  payment_id?: string | null;
  transfer_id?: string | null;
};

type SubscriptionStatementModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dict: DictionaryProps["dict"];
  initialDateRange?: DateRange;
  onDateRangeChange?: (range: DateRange) => void;
};

const STATUS_I18N: Record<string, [key: string, fallback: string]> = {
  done: ["Status completed", "Concluído"],
  success: ["Status completed", "Concluído"],
  completed: ["Status completed", "Concluído"],
  paid: ["Status completed", "Concluído"],
  released: ["Status released", "Liberado"],
  pending: ["Status pending", "Pendente"],
  pending_verification: ["Status pending", "Pendente"],
  awaiting: ["Status pending", "Pendente"],
  waiting: ["Status pending", "Pendente"],
  processing: ["Processing", "Processando"],
  in_process: ["Processing", "Processando"],
  in_progress: ["Processing", "Processando"],
  failed: ["Status failed", "Falhou"],
  error: ["Status failed", "Falhou"],
  refused: ["Status failed", "Falhou"],
  denied: ["Status failed", "Falhou"],
  canceled: ["Status canceled", "Cancelado"],
  cancelled: ["Status canceled", "Cancelado"],
};

const createDefaultPagination = (): PaginationState => ({
  asaas: { page: 1, perPage: 15 },
  manual: { page: 1, perPage: 15 },
  pix: { page: 1, perPage: 15 },
  checkout: { page: 1, perPage: 15 },
});

const getDefaultDateRange = (): DateRange => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return { start, end };
};

const toYMD = (date: Date) => {
  const target =
    date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, "0");
  const day = String(target.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const translateStatus = (
  status: string | undefined,
  dict: Record<string, string>,
) => {
  if (!status) return { label: "-", normalized: "" };
  const normalized = status.toLowerCase();
  const entry = STATUS_I18N[normalized];
  const label = entry ? dict[entry[0]] || entry[1] : status;
  return { label, normalized };
};

const getStatusBadgeClass = (normalized: string) => {
  switch (normalized) {
    case "done":
    case "success":
    case "completed":
    case "paid":
      return "bg-emerald-100 text-emerald-700";
    case "released":
      return "bg-emerald-100 text-emerald-700";
    case "pending":
    case "pending_verification":
    case "awaiting":
    case "waiting":
      return "bg-yellow-100 text-yellow-800";
    case "processing":
    case "in_process":
    case "in_progress":
      return "bg-sky-100 text-sky-700";
    case "failed":
    case "error":
    case "refused":
    case "denied":
      return "bg-red-100 text-red-700";
    case "canceled":
    case "cancelled":
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const parseDateValue = (value?: string | null) => {
  if (!value) return null;
  const trimmed = value.trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);
    return { date: localDate, hasTime: false };
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return { date: parsed, hasTime: trimmed.includes("T") };
};

const formatDateTime = (value?: string | null) => {
  const parsed = parseDateValue(value);
  if (!parsed) return "-";

  const { date, hasTime } = parsed;
  const baseOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
  };

  const options: Intl.DateTimeFormatOptions = hasTime
    ? { ...baseOptions, ...timeOptions }
    : baseOptions;

  return new Intl.DateTimeFormat("pt-BR", options).format(date);
};

const formatPaymentMethod = (
  method: string | undefined,
  dict: Record<string, string>,
) => {
  const normalized = (method || "").toLowerCase();
  if (!normalized) return "-";
  if (["pix"].includes(normalized)) return "PIX";
  if (["credit_card", "creditcard", "card"].includes(normalized)) {
    return dict["Card short"] || "Cartão";
  }
  if (["boleto", "bank_slip"].includes(normalized)) {
    return dict["Bank slip"] || "Boleto";
  }
  return method ?? "-";
};

const formatTransferLabel = (value?: string) => {
  if (!value) return "-";
  return value
    .split("_")
    .map(
      (chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase(),
    )
    .join(" ");
};

const formatKeyType = (
  value: string | undefined,
  dict: Record<string, string>,
) => {
  const normalized = (value || "").toLowerCase();
  switch (normalized) {
    case "cpf":
      return "CPF";
    case "cnpj":
      return "CNPJ";
    case "email":
      return dict["Email key type"] || "E-mail";
    case "phone":
      return dict["Phone key type"] || "Telefone";
    case "random":
      return dict["Random key"] || "Chave aleatória";
    default:
      return value ?? "-";
  }
};

const buildPaginationData = (pagination?: PaginationMeta | null) => {
  const totalPages = Math.max(pagination?.total_pages ?? 1, 1);
  const currentPage = Math.min(pagination?.page ?? 1, totalPages);
  const pagesArray = Array.from({ length: totalPages }, (_, idx) => idx + 1);
  return { currentPage, totalPages, pagesArray };
};

const SubscriptionStatementModal: React.FC<SubscriptionStatementModalProps> = ({
  open,
  onOpenChange,
  dict,
  initialDateRange,
  onDateRangeChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { hasPermission } = usePermission();
  const { isReady: isApiReady } = useApiReady(250);
  const { currency, establishment, baseURL } = useAuth();

  const { statement, statementBySource } = useSelector(
    (state: RootState) => state.subscriptionWallet,
  );

  const canReadSubscription = hasPermission("read subscription");
  const sourceLoading = statementBySource.loading;
  const summaryLoading = statement.loading;

  const [dateRange, setDateRange] = useState<DateRange>(
    initialDateRange ?? getDefaultDateRange(),
  );
  const [activeTab, setActiveTab] = useState<TabKey>("asaas");
  const [paginationState, setPaginationState] = useState<PaginationState>(
    createDefaultPagination,
  );

  const establishmentIdentifier = useMemo(() => {
    if (!establishment) {
      return null;
    }

    const hasEstablishmentInBaseURL = Boolean(
      baseURL?.includes("/establishment/"),
    );
    if (hasEstablishmentInBaseURL) {
      return null;
    }

    return establishment.slug ?? establishment.id ?? null;
  }, [baseURL, establishment]);

  const requestData = useCallback(
    async (
      range: DateRange,
      pagination: PaginationState,
      options?: { fetchSummary?: boolean },
    ) => {
      if (!open) return;
      if (!isApiReady || !canReadSubscription) return;

      const startDate = toYMD(range.start);
      const endDate = toYMD(range.end);

      const sourceParams = {
        start_date: startDate,
        end_date: endDate,
        page_manual: pagination.manual.page,
        per_page_manual: pagination.manual.perPage,
        page_asaas: pagination.asaas.page,
        per_page_asaas: pagination.asaas.perPage,
        page_pix: pagination.pix.page,
        per_page_pix: pagination.pix.perPage,
        page_checkout: pagination.checkout.page,
        per_page_checkout: pagination.checkout.perPage,
      };

      const promises: Array<Promise<unknown>> = [
        dispatch(
          fetchWalletStatementBySource({
            establishmentIdentifier,
            params: sourceParams,
          }),
        ).unwrap(),
      ];

      if (options?.fetchSummary) {
        promises.push(
          dispatch(
            fetchWalletStatement({
              params: {
                start_date: startDate,
                end_date: endDate,
                type: "all",
              },
            }),
          ).unwrap(),
        );
      }

      await Promise.all(promises);
    },
    [dispatch, establishmentIdentifier, isApiReady, canReadSubscription, open],
  );

  useEffect(() => {
    if (!initialDateRange) return;
    if (!open) return;
    setDateRange(initialDateRange);
  }, [initialDateRange, open]);

  useEffect(() => {
    if (!open) return;
    const initialRange = initialDateRange ?? getDefaultDateRange();
    const initialPagination = createDefaultPagination();
    setDateRange(initialRange);
    setPaginationState(initialPagination);
    setActiveTab("asaas");
    void requestData(initialRange, initialPagination);
  }, [open, requestData]);

  const handleDateRangeChange = useCallback(
    (nextRange: DateRange) => {
      setDateRange(nextRange);
      onDateRangeChange?.(nextRange);
    },
    [onDateRangeChange],
  );

  const handleApplyFilters = useCallback(
    (range?: DateRange) => {
      if (!open) return;
      const nextRange = range ?? dateRange;
      const nextPagination = createDefaultPagination();
      setDateRange(nextRange);
      setPaginationState(nextPagination);
      void requestData(nextRange, nextPagination);
    },
    [dateRange, open, requestData],
  );

  const handlePageChange = useCallback(
    (tab: SourceTabKey, page: number) => {
      if (!open) return;
      setPaginationState((prev) => {
        const next = {
          ...prev,
          [tab]: {
            ...prev[tab],
            page: Math.max(page, 1),
          },
        } as PaginationState;
        void requestData(dateRange, next, { fetchSummary: false });
        return next;
      });
    },
    [dateRange, open, requestData],
  );

  const handleAllPageChange = useCallback(
    async (page: number) => {
      if (!open) return;
      if (!isApiReady || !canReadSubscription) return;

      await dispatch(
        fetchWalletStatement({
          params: {
            start_date: toYMD(dateRange.start),
            end_date: toYMD(dateRange.end),
            type: "all",
            page: Math.max(page, 1),
            per_page: Math.max(Number(statement.pagination?.per_page ?? 15), 1),
          },
        }),
      );
    },
    [
      dispatch,
      isApiReady,
      canReadSubscription,
      dateRange,
      open,
      statement.pagination,
    ],
  );

  const summaryCards = statement.cards as {
    total_entries?: number;
    total_withdrawals?: number;
    period_balance?: number;
  } | null;

  const walletSummary = useMemo(
    () => ({
      entries: formatCurrency(
        Number(summaryCards?.total_entries ?? 0),
        currency,
      ),
      outflows: formatCurrency(
        Number(summaryCards?.total_withdrawals ?? 0),
        currency,
      ),
      balance: formatCurrency(
        Number(summaryCards?.period_balance ?? 0),
        currency,
      ),
    }),
    [summaryCards, currency],
  );

  const manualBlock = statementBySource.manualEntries;
  const asaasBlock = statementBySource.asaasEntries;
  const checkoutBlock = statementBySource.checkoutEntries;
  const pixBlock = statementBySource.pixWithdrawals;
  const sourceCards = statementBySource.cards;
  const checkoutTabEnabled = isScheduleCheckoutFeatureEnabled();
  const allItems = ((statement.items ?? []) as WalletStatementItem[]) || [];
  const allPagination = (statement.pagination as PaginationMeta | null) ?? null;

  const entriesValue = summaryLoading
    ? dict["Loading summary"] || "Carregando..."
    : walletSummary.entries;
  const outflowsValue = summaryLoading
    ? dict["Loading summary"] || "Carregando..."
    : walletSummary.outflows;
  const balanceValue = summaryLoading
    ? dict["Loading summary"] || "Carregando..."
    : walletSummary.balance;

  const renderPaginationControls = useCallback(
    (tab: SourceTabKey, pagination?: PaginationMeta | null) => {
      const { currentPage, totalPages, pagesArray } =
        buildPaginationData(pagination);
      if (totalPages <= 1) {
        return null;
      }

      return (
        <div className="mt-4 flex justify-center sm:justify-end">
          <Pagination
            currentPage={currentPage}
            pages={pagesArray}
            totalPages={totalPages}
            onClick={(pageNumber) => handlePageChange(tab, pageNumber)}
          />
        </div>
      );
    },
    [handlePageChange],
  );

  const renderSourceCards = useCallback(
    (tab: SourceTabKey) => {
      if (!sourceCards) {
        return null;
      }

      const formatValue = (value?: number | null) =>
        formatCurrency(Number(value ?? 0), currency);

      if (tab === "asaas" || tab === "checkout") {
        const metrics =
          tab === "asaas" ? sourceCards.asaas : sourceCards.checkout;
        if (!metrics) return null;

        const cards = [
          {
            key: `${tab}-gross`,
            title: dict["Gross amount"] || "Valor bruto",
            value: formatValue(metrics.gross_amount),
            colorValue: "blue" as const,
          },
          {
            key: `${tab}-net`,
            title: dict["Net amount"] || "Valor líquido",
            value: formatValue(metrics.net_amount),
            colorValue: "green" as const,
          },
          {
            key: `${tab}-fees`,
            title: dict.Fees || dict["Fees"] || "Taxas",
            value: formatValue(metrics.total_fees),
            colorValue: "red" as const,
          },
        ];

        return (
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {cards.map(({ key, title, value, colorValue }) => (
              <CardStatistics
                key={key}
                title={title}
                value={value}
                colorValue={colorValue}
                className="border border-slate-100 bg-white shadow-sm"
              />
            ))}
          </div>
        );
      }

      if (tab === "manual") {
        const metrics = sourceCards.manual;
        if (!metrics) return null;

        return (
          <div className="mb-4 grid grid-cols-1 gap-4">
            <CardStatistics
              key="manual-total"
              title={dict["Manual total"] || "Total manual"}
              value={formatValue(metrics.total_amount)}
              colorValue="green"
              className="border border-slate-100 bg-white shadow-sm"
            />
          </div>
        );
      }

      if (tab === "pix") {
        const metrics = sourceCards.pix;
        if (!metrics) return null;

        return (
          <div className="mb-4 grid grid-cols-1 gap-4">
            <CardStatistics
              key="pix-total"
              title={dict["Withdrawals total"] || "Total saques"}
              value={formatValue(metrics.total_withdrawals)}
              colorValue="blue"
              className="border border-slate-100 bg-white shadow-sm"
            />
          </div>
        );
      }

      return null;
    },
    [currency, dict, sourceCards],
  );

  const renderEntriesContent = useCallback(
    (
      items: StatementBySourceEntry[],
      pagination: PaginationMeta | null | undefined,
      tabKey: TabKey,
    ) => {
      if (sourceLoading) {
        return (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        );
      }

      if (!items || items.length === 0) {
        return (
          <NotFound
            description={
              dict["Oops! No record found here"] ||
              "Ops! Nenhum registro encontrado aqui"
            }
          />
        );
      }

      const hideReleasedAndFee = tabKey === "asaas" || tabKey === "checkout";
      const showSubscriptionFee = tabKey === "asaas";
      const descriptionColumnLabel =
        tabKey === "checkout"
          ? dict["Schedule description column"] || "Agendamento / Descrição"
          : dict["Plan description column"] || "Plano / Descrição";
      const feeColumnLabel = dict.Fee || dict["Fee"] || "Taxa";
      const formatFeeAmount = (item: StatementBySourceEntry) =>
        item.split_amount != null
          ? formatCurrency(Number(item.split_amount), currency)
          : "-";

      return (
        <>
          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 px-2">{dict.Date || "Data"}</th>
                  <th className="py-2 px-2">{dict.Client || "Cliente"}</th>
                  <th className="py-2 px-2">{descriptionColumnLabel}</th>
                  <th className="py-2 px-2">
                    {dict["Paid amount"] || "Valor pago"}
                  </th>
                  {showSubscriptionFee && (
                    <th className="py-2 px-2">{feeColumnLabel}</th>
                  )}
                  {!hideReleasedAndFee && (
                    <>
                      <th className="py-2 px-2">
                        {dict["Released value"] || "Valor liberado"}
                      </th>
                      <th className="py-2 px-2">{feeColumnLabel}</th>
                    </>
                  )}
                  <th className="py-2 px-2">{dict.Status || "Status"}</th>
                  <th className="py-2 px-2">
                    {dict["Payment method"] || "Método"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const { label: statusLabel, normalized: statusNorm } =
                    translateStatus(item.status, dict);
                  const badgeClass = getStatusBadgeClass(statusNorm);
                  const releaseDate = item.released_at || item.release_date;
                  return (
                    <tr
                      key={`${tabKey}-${item.payment_id ?? index}`}
                      className="border-b"
                    >
                      <td className="py-2 px-2">
                        <div className="flex flex-col gap-1">
                          <span>{formatDateTime(item.date)}</span>
                          {releaseDate && (
                            <span className="text-xs text-orange-500">
                              {dict["Release prefix"] || "Liberação:"}{" "}
                              {formatDateTime(releaseDate)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        <span className="font-medium text-gray-900">
                          {item.client_name || "-"}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex flex-col gap-1 text-sm text-gray-700">
                          <span>{item.plan_name || "-"}</span>
                          {item.description && (
                            <span className="text-xs text-gray-500">
                              {item.description}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-2">
                        {formatCurrency(Number(item.amount ?? 0), currency)}
                      </td>
                      {showSubscriptionFee && (
                        <td className="py-2 px-2">{formatFeeAmount(item)}</td>
                      )}
                      {!hideReleasedAndFee && (
                        <>
                          <td className="py-2 px-2">
                            {item.released_amount != null
                              ? formatCurrency(
                                  Number(item.released_amount),
                                  currency,
                                )
                              : "-"}
                          </td>
                          <td className="py-2 px-2">{formatFeeAmount(item)}</td>
                        </>
                      )}
                      <td className="py-2 px-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${badgeClass}`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex flex-col gap-1 text-xs">
                          <span className="font-medium text-gray-700">
                            {formatPaymentMethod(item.payment_method, dict)}
                          </span>
                          {item.gateway && (
                            <span className="text-gray-500">
                              {item.gateway}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 lg:hidden">
            {items.map((item, index) => {
              const { label: statusLabel, normalized: statusNorm } =
                translateStatus(item.status, dict);
              const badgeClass = getStatusBadgeClass(statusNorm);
              const releaseDate = item.released_at || item.release_date;
              return (
                <div
                  key={`${tabKey}-mobile-${item.payment_id ?? index}`}
                  className="w-full overflow-hidden rounded-xl border p-4 shadow-sm border-border bg-card"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {item.client_name || "-"}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(item.date)}
                      </p>
                      {releaseDate && (
                        <p className="text-xs text-orange-500">
                          {dict["Release prefix"] || "Liberação:"}{" "}
                          {formatDateTime(releaseDate)}
                        </p>
                      )}
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${badgeClass}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>
                        {tabKey === "checkout"
                          ? dict.Appointment ||
                            dict["Appointment"] ||
                            "Agendamento"
                          : dict["Plan"] || dict["Current plan"] || "Plano"}
                      </span>
                      <span>{item.plan_name || "-"}</span>
                    </div>
                    {item.description && (
                      <p className="text-xs text-gray-500">
                        {item.description}
                      </p>
                    )}
                    <div className="flex justify-between">
                      <span>{dict["Paid amount"] || "Valor pago"}</span>
                      <span>
                        {formatCurrency(Number(item.amount ?? 0), currency)}
                      </span>
                    </div>
                    {showSubscriptionFee && (
                      <div className="flex justify-between">
                        <span>{feeColumnLabel}</span>
                        <span>{formatFeeAmount(item)}</span>
                      </div>
                    )}
                    {!hideReleasedAndFee && (
                      <>
                        <div className="flex justify-between">
                          <span>
                            {dict["Released value"] || "Valor liberado"}
                          </span>
                          <span>
                            {item.released_amount != null
                              ? formatCurrency(
                                  Number(item.released_amount),
                                  currency,
                                )
                              : "-"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>
                            {dict["Passed through amount"] || "Valor repassado"}
                          </span>
                          <span>{formatFeeAmount(item)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between">
                      <span>{dict["Payment method"] || "Método"}</span>
                      <span>
                        {formatPaymentMethod(item.payment_method, dict)}
                      </span>
                    </div>
                    {item.gateway && (
                      <div className="flex justify-between">
                        <span>{dict.Gateway || "Gateway"}</span>
                        <span>{item.gateway}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {renderPaginationControls(tabKey, pagination)}
        </>
      );
    },
    [currency, dict, renderPaginationControls, sourceLoading],
  );

  const renderPixContent = useCallback(
    (
      items: PixWithdrawalItem[],
      pagination: PaginationMeta | null | undefined,
    ) => {
      if (sourceLoading) {
        return (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        );
      }

      if (!items || items.length === 0) {
        return (
          <NotFound
            description={
              dict["No withdrawals found"] ||
              "Nenhum saque encontrado para o período"
            }
          />
        );
      }

      return (
        <>
          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 px-2">
                    {dict["Requested on"] || "Solicitado em"}
                  </th>
                  <th className="py-2 px-2">
                    {dict["Processed on"] || "Processado em"}
                  </th>
                  <th className="py-2 px-2">{dict.Amount || "Valor"}</th>
                  <th className="py-2 px-2">{dict.Status || "Status"}</th>
                  <th className="py-2 px-2">{dict.Type || "Tipo"}</th>
                  <th className="py-2 px-2">{dict["Key label"] || "Chave"}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const { label: statusLabel, normalized: statusNorm } =
                    translateStatus(item.status, dict);
                  const badgeClass = getStatusBadgeClass(statusNorm);
                  return (
                    <tr key={`pix-${index}`} className="border-b">
                      <td className="py-2 px-2">
                        {formatDateTime(item.requested_at)}
                      </td>
                      <td className="py-2 px-2">
                        {formatDateTime(item.processed_at)}
                      </td>
                      <td className="py-2 px-2">
                        {formatCurrency(Number(item.amount ?? 0), currency)}
                      </td>
                      <td className="py-2 px-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${badgeClass}`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                      <td className="py-2 px-2">
                        {formatTransferLabel(item.transfer_type)}
                      </td>
                      <td className="py-2 px-2">
                        <div className="flex flex-col text-xs text-gray-700">
                          <span className="font-medium">
                            {formatKeyType(item.key_type, dict)}
                          </span>
                          <span className="text-gray-500">
                            {item.key || "-"}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 lg:hidden">
            {items.map((item, index) => {
              const { label: statusLabel, normalized: statusNorm } =
                translateStatus(item.status, dict);
              const badgeClass = getStatusBadgeClass(statusNorm);
              return (
                <div
                  key={`pix-mobile-${index}`}
                  className="w-full overflow-hidden rounded-xl border p-4 shadow-sm border-border bg-card"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {formatCurrency(Number(item.amount ?? 0), currency)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Solicitado: {formatDateTime(item.requested_at)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Processado: {formatDateTime(item.processed_at)}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${badgeClass}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  <div className="mt-3 space-y-1 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>Tipo</span>
                      <span>{formatTransferLabel(item.transfer_type)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Chave ({formatKeyType(item.key_type, dict)})</span>
                      <span className="truncate text-right text-gray-600">
                        {item.key || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {renderPaginationControls("pix", pagination)}
        </>
      );
    },
    [currency, dict, renderPaginationControls, sourceLoading],
  );

  const renderAllContent = useCallback(
    (items: WalletStatementItem[], pagination: PaginationMeta | null) => {
      if (summaryLoading) {
        return (
          <div className="flex justify-center py-6">
            <Spinner />
          </div>
        );
      }

      if (!items || items.length === 0) {
        return (
          <NotFound
            description={
              dict["Oops! No record found here"] ||
              "Ops! Nenhum registro encontrado aqui"
            }
          />
        );
      }

      const { currentPage, totalPages, pagesArray } =
        buildPaginationData(pagination);

      return (
        <>
          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 px-2">{dict.Date || "Data"}</th>
                  <th className="py-2 px-2">
                    {dict.Description || "Descrição"}
                  </th>
                  <th className="py-2 px-2">{dict.Type || "Tipo"}</th>
                  {/*<th className="py-2 px-2">{dict.Status || "Status"}</th>*/}
                  <th className="py-2 px-2">{dict.Amount || "Valor"}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => {
                  const { label: statusLabel, normalized: statusNorm } =
                    translateStatus(item.status, dict);
                  const badgeClass = getStatusBadgeClass(statusNorm);
                  const signedValue =
                    item.signed_value != null
                      ? item.signed_value
                      : (item.value ?? 0);
                  const valueClass =
                    signedValue < 0 ? "text-rose-600" : "text-emerald-700";
                  return (
                    <tr key={`all-${item.id ?? index}`} className="border-b">
                      <td className="py-2 px-2">{formatDateTime(item.date)}</td>
                      <td className="py-2 px-2">{item.description || "-"}</td>
                      {/* <td className="py-2 px-2">{item.transaction_type || "-"}</td> */}
                      <td className="py-2 px-2">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${badgeClass}`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                      <td className={`py-2 px-2 font-medium ${valueClass}`}>
                        {formatCurrency(Number(signedValue), currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 lg:hidden">
            {items.map((item, index) => {
              const { label: statusLabel, normalized: statusNorm } =
                translateStatus(item.status, dict);
              const badgeClass = getStatusBadgeClass(statusNorm);
              const signedValue =
                item.signed_value != null
                  ? item.signed_value
                  : (item.value ?? 0);
              const valueClass =
                signedValue < 0 ? "text-rose-600" : "text-emerald-700";
              return (
                <div
                  key={`all-mobile-${item.id ?? index}`}
                  className="w-full overflow-hidden rounded-xl border p-4 shadow-sm border-border bg-card"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs text-gray-500">
                        {formatDateTime(item.date)}
                      </p>
                      <p className="text-sm font-medium text-gray-900">
                        {item.transaction_type || "-"}
                      </p>
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${badgeClass}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-700">
                    {item.description || "-"}
                  </p>
                  <p
                    className={`mt-3 text-right text-sm font-semibold ${valueClass}`}
                  >
                    {formatCurrency(Number(signedValue), currency)}
                  </p>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex justify-center sm:justify-end">
              <Pagination
                currentPage={currentPage}
                pages={pagesArray}
                totalPages={totalPages}
                onClick={handleAllPageChange}
              />
            </div>
          )}
        </>
      );
    },
    [currency, dict, handleAllPageChange, summaryLoading],
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      titleModal={dict["Wallet statement title"] || "Extrato da carteira"}
      size="wide"
      height="large"
    >
      {!canReadSubscription ? (
        <p className="text-sm text-muted-foreground">
          {dict["No permission view wallet statement"] ||
            "Você não possui permissão para visualizar o extrato."}
        </p>
      ) : (
        <div className="space-y-5 sm:space-y-6">
          <div className="w-full min-w-0">
            <FilterDate
              value={dateRange}
              onChange={handleDateRangeChange}
              onSubmitFilter={handleApplyFilters}
              applyLabel={dict["Apply"] || "Aplicar"}
            />
          </div>

          <Tabs
            value={activeTab}
            onValueChange={(value) => setActiveTab(value as TabKey)}
            className="w-full min-w-0"
          >
            {" "}
            <TabsList
              className={`flex h-auto w-full min-w-0 flex-nowrap justify-start gap-1.5 overflow-x-auto rounded-xl border p-1.5 [-ms-overflow-style:none] [scrollbar-width:none] border-border bg-card sm:grid sm:h-9 sm:w-full ${
                checkoutTabEnabled ? "sm:grid-cols-4" : "sm:grid-cols-3"
              } sm:gap-0 sm:overflow-visible sm:rounded-lg sm:border-0 sm:bg-muted sm:p-[3px] [&::-webkit-scrollbar]:hidden`}
            >
              {/* <TabsTrigger
                value="all"
                className="min-h-[44px] min-w-[7.5rem] shrink-0 flex-col gap-0.5 px-3 py-2 sm:min-h-0 sm:min-w-0 sm:flex-row sm:gap-2 sm:px-2 sm:py-1"
              >
                <span className="flex flex-col items-center gap-0.5 text-center sm:flex-row sm:gap-2 sm:text-left">
                  <Wallet className="h-4 w-4 shrink-0 text-indigo-500" />
                  <span className="text-xs font-medium sm:text-sm">
                    {dict["General tab"] || "Geral"}
                  </span>
                </span>
              </TabsTrigger> */}
              <TabsTrigger
                value="asaas"
                className="min-h-[44px] min-w-[7.5rem] shrink-0 flex-col gap-0.5 px-3 py-2 sm:min-h-0 sm:min-w-0 sm:flex-row sm:gap-2 sm:px-2 sm:py-1"
              >
                <span className="flex flex-col items-center gap-0.5 text-center sm:flex-row sm:gap-2 sm:text-left">
                  <TrendingUp className="h-4 w-4 shrink-0 text-emerald-500" />
                  <span className="text-xs font-medium sm:text-sm">
                    <span className="sm:hidden">
                      {dict["Subscriptions short tab"] || "Assin."}
                    </span>
                    <span className="hidden sm:inline">
                      {dict["Subscriptions tab"] || "Assinaturas"}
                    </span>
                  </span>
                </span>
              </TabsTrigger>
              {checkoutTabEnabled ? (
                <TabsTrigger
                  value="checkout"
                  className="min-h-[44px] min-w-[7.5rem] shrink-0 flex-col gap-0.5 px-3 py-2 sm:min-h-0 sm:min-w-0 sm:flex-row sm:gap-2 sm:px-2 sm:py-1"
                >
                  <span className="flex flex-col items-center gap-0.5 text-center sm:flex-row sm:gap-2 sm:text-left">
                    <CalendarClock className="h-4 w-4 shrink-0 text-sky-500" />
                    <span className="text-xs font-medium sm:text-sm">
                      {dict["Checkout tab"] || "Checkout"}
                    </span>
                  </span>
                </TabsTrigger>
              ) : null}
              <TabsTrigger
                value="manual"
                className="min-h-[44px] min-w-[7.5rem] shrink-0 flex-col gap-0.5 px-3 py-2 sm:min-h-0 sm:min-w-0 sm:flex-row sm:gap-2 sm:px-2 sm:py-1"
              >
                <span className="flex flex-col items-center gap-0.5 text-center sm:flex-row sm:gap-2 sm:text-left">
                  <Wallet className="h-4 w-4 shrink-0 text-emerald-500" />
                  <span className="text-xs font-medium sm:text-sm">
                    <span className="sm:hidden">
                      {dict["Manual subscriptions tab"]?.split(" ")[0] ||
                        "Manual"}
                    </span>
                    <span className="hidden sm:inline">
                      {dict["Manual subscriptions tab"] ||
                        "Assinaturas manuais"}
                    </span>
                  </span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="pix"
                className="min-h-[44px] min-w-[7.5rem] shrink-0 flex-col gap-0.5 px-3 py-2 sm:min-h-0 sm:min-w-0 sm:flex-row sm:gap-2 sm:px-2 sm:py-1"
              >
                <span className="flex flex-col items-center gap-0.5 text-center sm:flex-row sm:gap-2 sm:text-left">
                  <TrendingDown className="h-4 w-4 shrink-0 text-rose-500" />
                  <span className="text-xs font-medium sm:text-sm">
                    {dict["Withdrawals tab"] || "Saques"}
                  </span>
                </span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="all" className="mt-4">
              {renderAllContent(allItems, allPagination)}
            </TabsContent>
            <TabsContent value="asaas" className="mt-4">
              {renderSourceCards("asaas")}
              {renderEntriesContent(
                (asaasBlock.items as StatementBySourceEntry[]) || [],
                asaasBlock.pagination,
                "asaas",
              )}
            </TabsContent>
            {checkoutTabEnabled ? (
              <TabsContent value="checkout" className="mt-4">
                {renderSourceCards("checkout")}
                {renderEntriesContent(
                  (checkoutBlock.items as StatementBySourceEntry[]) || [],
                  checkoutBlock.pagination,
                  "checkout",
                )}
              </TabsContent>
            ) : null}
            <TabsContent value="manual" className="mt-4">
              {renderSourceCards("manual")}
              {renderEntriesContent(
                (manualBlock.items as StatementBySourceEntry[]) || [],
                manualBlock.pagination,
                "manual",
              )}
            </TabsContent>
            <TabsContent value="pix" className="mt-4">
              {renderSourceCards("pix")}
              {renderPixContent(
                (pixBlock.items as PixWithdrawalItem[]) || [],
                pixBlock.pagination,
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Modal>
  );
};

export default SubscriptionStatementModal;
